"use client";

import { Heading } from "@pamperedchef/design-system";
import type { BlockOf, WaysToUseItem } from "@/domain/catalog";
import * as styles from "./WaysToUse.module.scss";

const DEFAULT_HEADLINE = "Ways to Use";

/**
 * Adapter for the `ways_to_use_block` modular block.
 *
 * Per `docs/PDP_ARCHITECTURE.md` ("Content blocks"), this file is a thin
 * shell over UI: it unpacks the typed CMS payload and forwards display props.
 */
export function WaysToUse({ block }: { block: BlockOf<"ways_to_use_block"> }) {
	// A row with neither a title nor an image has nothing to label it, so it
	// is dropped rather than rendered as an unattributed paragraph.
	const items = (block.items ?? []).filter(
		(item: WaysToUseItem) =>
			Boolean(item.title) || Boolean(item.ways_use_image?.image)
	);

	if (items.length === 0) return null;

	const headline =
		block.headline && block.headline.length > 0
			? block.headline
			: DEFAULT_HEADLINE;

	return (
		<section
			className={styles["ways-to-use"]}
			data-block="ways_to_use_block"
		>
			<Heading tag="h2" className={styles["ways-to-use__headline"]}>
				{headline}
			</Heading>

			<ul className={styles["ways-to-use__list"]}>
				{items.map((item, i) => (
					<li
						key={`${item.title ?? "item"}-${i}`}
						className={styles["ways-to-use__item"]}
					>
						{item.ways_use_image?.image ? (
							<div className={styles["ways-to-use__image-wrap"]}>
								{/* eslint-disable-next-line @next/next/no-img-element */}
								<img
									className={styles["ways-to-use__image"]}
									src={item.ways_use_image.image}
									alt={
										item.ways_use_image.image_alt_text ?? ""
									}
									loading="lazy"
								/>
							</div>
						) : null}

						<div className={styles["ways-to-use__content"]}>
							{item.title ? (
								<Heading
									tag="h3"
									className={styles["ways-to-use__title"]}
								>
									{item.title}
								</Heading>
							) : null}
							{item.body ? (
								<p className={styles["ways-to-use__body"]}>
									{item.body}
								</p>
							) : null}
						</div>
					</li>
				))}
			</ul>
		</section>
	);
}
